import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {FormikHelpers} from "formik";
import Button from "../../../components/Button";
import {TrashIcon} from "@heroicons/react/solid";
import {useAuth} from "../../../context/AuthContext";
import {useOperator} from "../../../context/OperatorContext";
import {Building, BuildingData, BuildingProvider} from "../../../types/Building";
import {DeleteBuilding} from "../index";
import {BuildingForm} from "../";
import FeatureFlag from "../../../types/FeatureFlag";
import {stripProperties} from "../../../services/object";

function MutateBuilding() {
    const {id} = useParams()
    const {can, canUse} = useAuth();
    const {data: building, isLoading} = useQuery(
        ['buildings', {id}],
        () => ngage.buildings.get(Number(id)),
        {enabled: !!id && can('buildings:read')}
    )

    const store = useMutation((data: BuildingData) => ngage.buildings.store(data))
    const update = useMutation((data: BuildingData) => ngage.buildings.update(parseInt(id as string), data))

    const {termFor} = useOperator();
    const {notify} = useNotifications();
    const navigate = useNavigate();

    const [deletedBuilding, setDeletedBuilding] = useState<Building | null>(null);

    // Handle form submission
    async function handleSubmit(values: BuildingData, {setSubmitting}: FormikHelpers<BuildingData>) {
        // Prepare values for submission (excluding key from floors only used for React key)
        const newValues = {
            ...values,
            floors: [...values.floors?.map((floor) => stripProperties(floor, ['key'])) ?? []],
        }

        id ? await handleUpdate(newValues) : await handleStore(newValues)
        await invalidate('buildings.all');
        await invalidate(['buildings', {id}]);

        setSubmitting(false)
        navigate('/buildings');
    }

    // Handle store mutation
    async function handleStore(values: BuildingData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Building created", `${values.name} has been created`)
        } catch (error) {
            notify("error", "Building Not Created", `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: BuildingData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Building Updated", `${values.name} has been updated`)
            await invalidate(['users', {id}])
        } catch (error) {
            notify("error", "Building Not Updated", `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare action handlers
    function handleDeleteBuilding() {
        if (building) setDeletedBuilding(building)
    }

    // Prepare breadcrumbs
    const breadcrumbs = {
        'Location': '/',
        'Buildings': '/buildings',
        [`${id? 'Edit' : 'New'} ${termFor('Building')}`]: id? `/buildings/${id}/edit`: '/buildings/new'
    }

    // Prepare page actions
    const actions = <>
        {(can('buildings:write') && canUse(FeatureFlag.IndoorMapping) && id) &&
          <Button
            color={Button.colors.WHITE}
            icon={TrashIcon}
            onClick={handleDeleteBuilding}
            disabled={!building || building.provider !== BuildingProvider.NGAGE}
          >
              Delete
          </Button>
        }
    </>

    return (
        <Page title={`${id? 'Edit' : 'New'} ${termFor('Building')}`}
              breadcrumbs={breadcrumbs}
              isLoading={isLoading}
              actions={actions}
        >
            <BuildingForm building={building} onSubmit={handleSubmit}/>

            {/* Delete confirmation modal */}
            <DeleteBuilding
              show={(building != null) && (building === deletedBuilding)}
              building={deletedBuilding}
              onCancel={() => setDeletedBuilding(null)}
              onDelete={() => navigate('/buildings')}
              onDeleteFail={() => setDeletedBuilding(null)}
            />
        </Page>
    );
}

export default MutateBuilding;